import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SelectOption } from '@kula-ai/components';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';
import { http } from '@/lib/ats-https';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AtsCommTemplateAttachment = {
  id: number;
  filename: string;
  content_type?: string;
  byte_size?: number;
  url?: string;
};

export type AtsCommTemplate = {
  id: number;
  name: string;
  channel?: string;
  category?: string;
  subject?: string;
  body?: string;
  attachments?: AtsCommTemplateAttachment[];
};

export type AtsCommTemplatesListResponse = {
  data: AtsCommTemplate[];
  meta?: {
    count?: number;
    page?: number;
    items?: number;
    pages?: number;
  };
  errors?: unknown[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const ATS_COMM_TEMPLATE_CHANNEL_EMAIL = 'email' as const;

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

function buildPath(params: {
  channel: string;
  categories: readonly string[];
  items?: number;
  nameContains?: string;
}): string {
  const items = params.items ?? 200;
  const nameQ = params.nameContains ?? '';
  const catQs = params.categories
    .map((c) => `&filter_by[category][]=${encodeURIComponent(c)}`)
    .join('');
  return `/ats_comm_templates?filter_by[channel]=${encodeURIComponent(params.channel)}${catQs}&items=${items}&filter_by[name]=${encodeURIComponent(nameQ)}`;
}

export async function fetchAtsCommTemplates(params: {
  channel: string;
  categories: readonly string[];
  items?: number;
  nameContains?: string;
}): Promise<AtsCommTemplate[]> {
  const path = buildPath(params);
  const res = (await http.get(path)) as AtsCommTemplatesListResponse;
  assertResolvedAtsNotHttpError(res);
  return Array.isArray(res?.data) ? res.data : [];
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

export function mapAtsTemplatesToSelectOptions(
  rows: AtsCommTemplate[]
): SelectOption[] {
  return rows.map((t) => ({
    value: String(t.id),
    label: t.name,
    data: {
      body: t.body ?? '',
      subject: t.subject ?? '',
      attachments: t.attachments ?? [],
    },
  }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const templateQueryKeys = {
  all: ['ats-comm-templates'] as const,
  list: (channel: string, categories: readonly string[]) =>
    [...templateQueryKeys.all, 'list', channel, ...categories] as const,
};

export function useAtsCommTemplates(params: {
  channel: string;
  categories: readonly string[];
  items?: number;
  nameContains?: string;
}): UseQueryResult<AtsCommTemplate[], Error> {
  return useQuery({
    queryKey: templateQueryKeys.list(params.channel, params.categories),
    queryFn: () => fetchAtsCommTemplates(params),
    staleTime: 60_000,
  });
}
